import { X } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import type { AgentConfigListItem, AgentConfigUpdate } from '../../lib/api'
import { AgentConfigForm } from './AgentConfigForm'
import {
  useUpdateAgentConfig,
  useResetAgentConfig,
  useRestorePromptVersion,
} from '../../hooks/useAIConfig'

interface AgentConfigDrawerProps {
  agentId: string | null
  agents: AgentConfigListItem[]
  onClose: () => void
}

export function AgentConfigDrawer({ agentId, agents, onClose }: AgentConfigDrawerProps) {
  const updateAgent = useUpdateAgentConfig()
  const resetAgent = useResetAgentConfig()
  const restorePromptVersion = useRestorePromptVersion()

  const agent = agentId ? agents.find((a) => a.agent_id === agentId) ?? null : null

  const handleSave = async (agentKey: string, data: AgentConfigUpdate) => {
    await updateAgent.mutateAsync({ agentKey, data })
  }

  const handleReset = async (agentKey: string) => {
    await resetAgent.mutateAsync(agentKey)
  }

  const handleRestoreVersion = async (agentKey: string, promptId: string) => {
    await restorePromptVersion.mutateAsync({ agentKey, promptId })
  }

  return (
    <AnimatePresence>
      {agent && (
        <>
          {/* Overlay */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/20 z-40"
          />
          {/* Drawer */}
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 30, stiffness: 300 }}
            className="fixed right-0 top-0 h-full w-[520px] bg-white shadow-2xl z-50 flex flex-col"
          >
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 shrink-0">
              <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider">
                Configuracion del Agente
              </h3>
              <button
                type="button"
                onClick={onClose}
                className="p-1.5 rounded-md text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-6">
              <div className="card">
                <AgentConfigForm
                  agent={agent}
                  onSave={handleSave}
                  onReset={handleReset}
                  onRestorePromptVersion={handleRestoreVersion}
                  isSaving={updateAgent.isPending}
                  isResetting={resetAgent.isPending}
                  isRestoringPrompt={restorePromptVersion.isPending}
                />
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}
